/**
 * Checkbox primitive (T49.3).
 *
 * A real `<input type="checkbox">` wrapped in a `<label>` so the box + the label
 * text share one control: a tap/click anywhere on the row toggles it, the
 * accessible name is the label, and keyboard operability (Tab to reach, Space to
 * toggle) comes from the native input for free — preferred over a div-with-role.
 *
 * The box's visual treatment (the bordered square, the indigo checked fill + the
 * white check glyph, the keyboard focus ring) can't be expressed inline
 * (pseudo-elements + `:checked` + `:focus-visible`), so it lives in
 * `responsive.css` under the `.shopista-checkbox` class — the same approach the
 * BackSheet `.rec-cb` checkbox uses. This file owns the layout (the flex row, the
 * gap, the label type) and the markup/ARIA wiring; the stylesheet owns the box
 * pixels. Tokens-only (CP5): every value here is a `var(--...)` token.
 *
 * The optional `nudge` flag adds a one-shot attention state (an indigo ring + a
 * small horizontal shake, capped to ring-only under `prefers-reduced-motion`) for
 * the "you tried to continue without ticking" case — the calm nudge the Designer
 * spec calls for instead of a red error.
 */

import { forwardRef, useId } from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  /** The label content (text + inline links). Becomes the control's accessible name. */
  label: ReactNode;
  /**
   * One-shot attention state — draws the indigo ring + (motion-allowed) shake on
   * the box. Set true when the user pressed an action without ticking, so the row
   * pulls focus toward the one remaining step.
   */
  nudge?: boolean;
  /** Id of an element describing the box (e.g. the consent hint), wired to aria-describedby. */
  describedById?: string | undefined;
}

const rowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  // Two-line label keeps the box pinned to the first line.
  alignItems: 'flex-start',
  gap: 'var(--space-3)',
  // The full row is the tap target — comfortably ≥44px tall on a phone even though
  // the drawn box is 1.25rem (Designer spec §4 touch-target).
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const labelTextStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  // Normal (1.5): 1.5 × 14px = 21px line box vs. the 20px (1.25rem) drawn box,
  // so `align-items: flex-start` on the row centres the box on the first line
  // almost exactly.
  lineHeight: 'var(--line-height-normal)',
};

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, nudge, describedById, id, className, ...rest },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  // The box class carries the styled appearance; the nudge modifier adds the
  // attention ring/shake. Caller-supplied className is appended last.
  const boxClass = ['shopista-checkbox', nudge ? 'shopista-checkbox-nudge' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <label htmlFor={inputId} style={rowStyle}>
      <input
        {...rest}
        id={inputId}
        ref={ref}
        type="checkbox"
        className={boxClass}
        aria-describedby={describedById}
      />
      <span style={labelTextStyle}>{label}</span>
    </label>
  );
});

/**
 * CycleCheckbox (T55.17) — the shared tri-state filter-row control the Deals
 * Filters drawer renders for BOTH its Category tab (off → include → exclude)
 * and its Attribute tab (off → require → exclude): one component, one box
 * anatomy, two vocabularies. `Checkbox` above stays the binary primitive
 * (Stores tab, consent rows — a real `<input type="checkbox">`); this is a
 * non-native tri-state row, so it's a plain `<button>` — a real checkbox
 * input has no third visual state to draw.
 *
 * Anatomy (left → right): a bordered box glyph (empty / ✓ / −) · the term
 * label · an optional small state-label caption (e.g. "Included" /
 * "Required" / "Excluded" — the caller supplies the exact word, since
 * Categories and Attributes use different vocabularies for the same
 * "positive" cycle state) · a right-aligned count slot. `intent==='off'`
 * renders a bare, uncoloured row (no state label) — a plain unselected
 * option, matching the existing Category/Store checkbox row's resting look.
 *
 * Row + box containment (T55.15): `width:'100%'`, `boxSizing:'border-box'`,
 * `minWidth:0` on the outer button, and `overflowWrap:'anywhere'` on the
 * label — the same horizontal-overflow contract every other Filters-drawer
 * row already holds, since this component now renders as a direct child of
 * the drawer's scrolling option list.
 */
export type CycleCheckboxIntent = 'off' | 'positive' | 'negative';

export interface CycleCheckboxProps {
  /** The option's term — the row's visible + accessible label. */
  label: ReactNode;
  /** off (unselected) · positive (include·require, ✓) · negative (exclude, −, struck-through). */
  intent: CycleCheckboxIntent;
  /**
   * Small caption next to the count once `intent` leaves 'off' — e.g.
   * "Included"/"Required" for `positive`, "Excluded" for `negative`. Omit (or
   * pass an empty string) while `intent` is 'off'.
   */
  stateLabel?: string | undefined;
  /** Right-aligned deal-card count for this option. */
  count?: ReactNode;
  onClick: () => void;
  'data-testid'?: string;
  'aria-label'?: string;
}

const cycleRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  width: '100%',
  minHeight: '2.75rem',
  boxSizing: 'border-box',
  minWidth: 0,
  padding: '0 var(--space-3)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  background: 'transparent',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'var(--font-family-sans)',
};
const cyclePositiveRowStyle: CSSProperties = {
  ...cycleRowStyle,
  background: 'var(--color-accent-subtle)',
  color: 'var(--color-accent-primary)',
};
const cycleNegativeRowStyle: CSSProperties = {
  ...cycleRowStyle,
  background: 'var(--color-status-danger-subtle)',
  color: 'var(--color-status-danger)',
};
// Longhand border properties (not the `border` shorthand) so the positive/
// negative variants below can override just `borderColor` without React's
// shorthand-vs-longhand rerender warning (same fix as `DealsPage.tsx`'s
// `filterButtonStyle`/`filterButtonActiveStyle` pair).
const cycleBoxStyle: CSSProperties = {
  flex: '0 0 auto',
  width: '1.25rem',
  height: '1.25rem',
  boxSizing: 'border-box',
  display: 'grid',
  placeItems: 'center',
  borderWidth: 'var(--border-width-hairline)',
  borderStyle: 'solid',
  borderColor: 'var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 'var(--font-size-3xs)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-inverse)',
};
const cycleBoxPositiveStyle: CSSProperties = {
  ...cycleBoxStyle,
  borderColor: 'var(--color-accent-primary)',
  background: 'var(--color-accent-primary)',
};
const cycleBoxNegativeStyle: CSSProperties = {
  ...cycleBoxStyle,
  borderColor: 'var(--color-status-danger)',
  background: 'var(--color-status-danger)',
};
const cycleLabelStyle: CSSProperties = { flex: 1, minWidth: 0, overflowWrap: 'anywhere' };
const cycleLabelNegativeStyle: CSSProperties = { ...cycleLabelStyle, textDecorationLine: 'line-through' };
const cycleStateLabelStyle: CSSProperties = {
  flexShrink: 0,
  fontSize: 'var(--font-size-3xs)',
  color: 'var(--color-text-muted)',
};
const cycleCountStyle: CSSProperties = {
  flexShrink: 1,
  minWidth: 0,
  overflowWrap: 'anywhere',
  fontSize: 'var(--font-size-3xs)',
  color: 'var(--color-text-muted)',
};

export function CycleCheckbox({
  label,
  intent,
  stateLabel,
  count,
  onClick,
  ...rest
}: CycleCheckboxProps): JSX.Element {
  const glyph = intent === 'off' ? '' : intent === 'negative' ? '−' : '✓';
  const rowStyleFor =
    intent === 'negative' ? cycleNegativeRowStyle : intent === 'positive' ? cyclePositiveRowStyle : cycleRowStyle;
  const boxStyleFor = intent === 'negative' ? cycleBoxNegativeStyle : intent === 'positive' ? cycleBoxPositiveStyle : cycleBoxStyle;
  const labelStyleFor = intent === 'negative' ? cycleLabelNegativeStyle : cycleLabelStyle;

  return (
    <button type="button" style={rowStyleFor} onClick={onClick} {...rest}>
      <span style={boxStyleFor} aria-hidden="true">
        {glyph}
      </span>
      <span style={labelStyleFor}>{label}</span>
      {stateLabel ? <span style={cycleStateLabelStyle}>{stateLabel}</span> : null}
      {count !== undefined ? <span style={cycleCountStyle}>{count}</span> : null}
    </button>
  );
}
